/**
 * Supabase integration utilities
 * Handles connection management and key encryption
 */

import type { SupabaseIntegration } from '@/contexts/IntegrationsContext';
import { encryptString, decryptString } from './encryption';

/**
 * Creates a Supabase integration object from user-provided credentials
 */
export async function createSupabaseIntegration(
  id: string,
  name: string,
  projectUrl: string,
  anonKey: string,
  serviceRoleKey?: string
): Promise<SupabaseIntegration> {
  // Extract project reference from URL (e.g., https://xxxxx.supabase.co -> xxxxx)
  const projectRef = extractProjectRef(projectUrl);

  // Encrypt keys for secure storage
  const encryptedAnonKey = await encryptString(anonKey);
  const encryptedServiceRoleKey = serviceRoleKey
    ? await encryptString(serviceRoleKey)
    : undefined;

  const now = Date.now();

  return {
    id,
    name,
    type: 'supabase',
    enabled: true,
    createdAt: now,
    updatedAt: now,
    projectUrl,
    anonKey: encryptedAnonKey,
    serviceRoleKey: encryptedServiceRoleKey,
    projectRef,
    metadata: {},
  };
}

/**
 * Extracts the project reference ID from a Supabase URL
 */
export function extractProjectRef(projectUrl: string): string {
  try {
    const url = new URL(projectUrl);
    // Extract subdomain from URL like https://xxxxx.supabase.co
    const match = url.hostname.match(/^([^.]+)\.supabase\./);
    if (!match) {
      throw new Error('Invalid Supabase URL format');
    }
    return match[1];
  } catch (error) {
    throw new Error(`Failed to extract project reference from URL: ${projectUrl}`);
  }
}

/**
 * Decrypts and returns the Supabase anon key
 */
export async function getSupabaseAnonKey(integration: SupabaseIntegration): Promise<string> {
  return decryptString(integration.anonKey);
}

/**
 * Decrypts and returns the Supabase service role key (if available)
 */
export async function getSupabaseServiceRoleKey(
  integration: SupabaseIntegration
): Promise<string | undefined> {
  if (!integration.serviceRoleKey) return undefined;
  return decryptString(integration.serviceRoleKey);
}

/**
 * Validates a Supabase connection by making a test query
 */
export async function validateSupabaseConnection(
  projectUrl: string,
  anonKey: string
): Promise<boolean> {
  try {
    // Simple health check - try to access the Supabase REST API
    const response = await fetch(`${projectUrl}/rest/v1/`, {
      headers: {
        'apikey': anonKey,
        'Authorization': `Bearer ${anonKey}`,
      },
    });

    // Any response (including 404) means the connection is valid
    // Only network errors or invalid keys will fail
    return response.status !== 401 && response.status !== 403;
  } catch (error) {
    console.error('Supabase connection validation failed:', error);
    return false;
  }
}

/**
 * Fetches database schema information from Supabase
 */
export async function fetchSupabaseSchema(
  projectUrl: string,
  serviceRoleKey: string
): Promise<any> {
  try {
    // Use the Supabase Management API to get schema information
    // This requires service role key
    const response = await fetch(`${projectUrl}/rest/v1/`, {
      headers: {
        'apikey': serviceRoleKey,
        'Authorization': `Bearer ${serviceRoleKey}`,
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch schema: ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Failed to fetch Supabase schema:', error);
    throw error;
  }
}

/**
 * Generates TypeScript types from Supabase schema
 * This would typically use the Supabase CLI, but for now returns a template
 */
export function generateSupabaseTypes(projectRef: string): string {
  return `export interface Database {
  public: {
    Tables: {
      // Add your table types here
      // Run 'npx supabase gen types typescript --project-id ${projectRef}' to generate actual types
    };
    Views: {
      // Add your view types here
    };
    Functions: {
      // Add your function types here
    };
  };
}`;
}

/**
 * Validates that a Supabase integration is still active
 */
export async function validateSupabaseIntegration(
  integration: SupabaseIntegration
): Promise<boolean> {
  try {
    const anonKey = await getSupabaseAnonKey(integration);
    return await validateSupabaseConnection(integration.projectUrl, anonKey);
  } catch (error) {
    console.error('Supabase integration validation failed:', error);
    return false;
  }
}

/**
 * Generates environment variables for a Supabase integration
 */
export function generateSupabaseEnvVars(
  projectUrl: string,
  anonKey: string
): Record<string, string> {
  return {
    VITE_SUPABASE_URL: projectUrl,
    VITE_SUPABASE_ANON_KEY: anonKey,
  };
}

/**
 * Generates Supabase client code for a project
 */
export function generateSupabaseClientCode(projectRef: string): string {
  return `import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);
`;
}

/**
 * Generates a React hook for querying a Supabase table
 */
export function generateSupabaseTableHook(tableName: string): string {
  const hookName = `use${tableName.charAt(0).toUpperCase() + tableName.slice(1)}`;
  
  return `import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export function ${hookName}() {
  return useQuery({
    queryKey: ['${tableName}'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('${tableName}')
        .select('*');
      
      if (error) throw error;
      return data;
    },
  });
}

export function ${hookName}ById(id: string) {
  return useQuery({
    queryKey: ['${tableName}', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('${tableName}')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });
}
`;
}

/**
 * Generates a React hook for mutating a Supabase table
 */
export function generateSupabaseTableMutation(tableName: string): string {
  const capitalizedName = tableName.charAt(0).toUpperCase() + tableName.slice(1);
  
  return `import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export function useCreate${capitalizedName}() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: result, error } = await supabase
        .from('${tableName}')
        .insert(data)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['${tableName}'] });
    },
  });
}

export function useUpdate${capitalizedName}() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      const { data: result, error } = await supabase
        .from('${tableName}')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['${tableName}'] });
    },
  });
}

export function useDelete${capitalizedName}() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('${tableName}')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['${tableName}'] });
    },
  });
}
`;
}
